import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import {
  getChallengeParticipantByIdAPI,
  putChallengeParticipantAPI,
} from '@/src/apis/rest/challenge';
import SectionLabel from '@/components/molecule/SectionLabel';
import Badge from '@/components/atom/Badge';
import dayjs from 'dayjs';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import type { Swiper as SwiperClass } from 'swiper/types';
import { Pagination } from 'swiper/modules';
import useDialog, { DialogType } from '@/hooks/useDialog';
import { TOAST_CONST } from '@/src/constants/toast';
import { toast } from '@/hooks/useToast';
import { ChallengeParticipantDto } from '@/src/models/ChallengeParticipantDto';
import LocalIcon from '@/components/atom/Icons/LocalIcon';
import EmptyCircle from '@/assets/icons/icon-empty-circle.svg';
import Typo from '@/components/atom/Typo';
import { OPTIONS_CONST } from '@/src/constants/options';

interface ParticipantDetailModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  challengeId: number | null;
  customerId: number | null;
}

const DataContainer = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex max-h-[90vh] min-h-[508px] w-[50%] flex-col items-center justify-start gap-y-3 rounded-[16px] border border-gray100 px-[20px] py-6">
      <header className="mb-[8px]">
        <Typo typoType="b16b">{title}</Typo>
      </header>
      {children}
    </div>
  );
};

const DataSection = ({ data }: { data: ChallengeParticipantDto }) => {
  return (
    <div className="col-span-1 flex flex-col gap-y-3">
      <SectionLabel
        key="createdAt"
        label="참여 일시"
        value={dayjs(data?.createdAt).format('YYYY-MM-DD HH:mm:ss')}
      />
      <SectionLabel
        key="isSuccess"
        label="참여 상태"
        value={
          data?.isSuccess ? (
            <Badge badgeColor="lightGreen" textBadgeColor="green">
              {OPTIONS_CONST.success}
            </Badge>
          ) : (
            <Badge badgeColor="lightRed" textBadgeColor="red">
              {OPTIONS_CONST.failed}
            </Badge>
          )
        }
      />
      <SectionLabel
        key="updatedAt"
        label="상태 변경 일시"
        value={dayjs(data?.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
      />
    </div>
  );
};

const ParticipantEmpty = ({ text }: { text: string }) => {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-[24px]">
      <LocalIcon iconSrc={EmptyCircle} iconSize={73} />
      <Typo typoType="b16r" typoColor="gray4">
        {text}
      </Typo>
    </div>
  );
};

export default function ParticipantDetailModal({
  open: openProps,
  onOpenChange,
  challengeId,
  customerId,
}: ParticipantDetailModalProps) {
  // state
  const [selectedFailIndex, setSelectedFailIndex] = useState<number>(0);

  const handleCloseModal = () => {
    onOpenChange(false);
  };

  const {
    Dialog: ConfirmDialog,
    closeDialog,
    openDialog,
    isOpen,
  } = useDialog({
    type: DialogType.ALERT,
  });

  // query
  const { data: detailData, refetch: refetchDetailData } = useQuery({
    queryKey: ['challenge-participants', customerId, challengeId],
    queryFn: () => {
      return getChallengeParticipantByIdAPI({
        challengeId: challengeId || 0,
        customerId: customerId || 0,
        page: 1,
        pageSize: 9999,
      });
    },
    enabled: !!challengeId || !!customerId,
  });

  const successData = useMemo(
    () => detailData?.rows.find(d => d.isSuccess),
    [detailData],
  );
  const failData = useMemo(
    () => detailData?.rows.filter(d => !d.isSuccess),
    [detailData],
  );

  // mutation
  const putMutation = useMutation({
    mutationFn: (id: number) => putChallengeParticipantAPI(id),
  });

  const handleSubmitPut = () => {
    const id = successData?.id;
    putMutation.mutate(Number(id), {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.rejectSuccess.title,
          description: TOAST_CONST.rejectSuccess.description,
        });
        refetchDetailData();
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.close}
        </Button>
        <Button
          disabled={!successData}
          type="submit"
          className="flex-1"
          onClick={() => openDialog()}
        >
          챌린지 도전 실패 처리하기
        </Button>
      </div>
    );
  };

  return (
    <>
      <ConfirmDialog
        open={isOpen}
        onOpenChange={closeDialog}
        title="챌린지 참여 상태 변경 처리"
        onOk={handleSubmitPut}
        okButtonProps={{ disabled: !successData }}
      >
        사용자가 챌린지를 재도전할 수 있도록 반려 처리하시겠습니까?
        <br />
        상태가 변경되어도 지급된 크레딧 회수 및 추가 지급되지 않습니다.
      </ConfirmDialog>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          className: 'max-w-[967px]',
        }}
        title={
          <div className="flex items-center gap-2">참여 정보 자세히 보기</div>
        }
        footer={renderFooter()}
      >
        <div className="flex gap-6">
          <DataContainer title="성공한 챌린지">
            {successData && (
              <>
                <div className="flex h-[320px] justify-center [&_.swiper-pagination]:flex [&_.swiper-pagination]:justify-center">
                  <img
                    style={{
                      width: 280,
                      height: 280,
                      objectFit: 'cover',
                      borderRadius: 12,
                    }}
                    width={280}
                    src={successData.challengeImage?.bufferFullUrl || ''}
                    height={280}
                    alt="slide image"
                  />
                </div>
                <DataSection data={successData} />
              </>
            )}
            {!successData && (
              <ParticipantEmpty text="성공한 챌린지가 없습니다" />
            )}
          </DataContainer>
          <DataContainer title="실패한 챌린지">
            {failData && failData?.length > 0 && (
              <>
                <div className="flex h-[320px] w-[280px] justify-center [&_.swiper-pagination]:flex [&_.swiper-pagination]:justify-center">
                  <Swiper
                    modules={[Pagination]}
                    spaceBetween={12}
                    slidesPerView={1}
                    pagination={{
                      clickable: true,
                    }}
                    onSlideChange={(swiper: SwiperClass) => {
                      setSelectedFailIndex(swiper.activeIndex);
                    }}
                  >
                    {failData?.map(d => {
                      return (
                        <SwiperSlide
                          key={d.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <img
                            style={{
                              width: 280,
                              height: 280,
                              objectFit: 'cover',
                              borderRadius: 12,
                            }}
                            width={280}
                            src={d.challengeImage?.bufferFullUrl || ''}
                            height={280}
                            alt="slide image"
                          />
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
                <DataSection data={failData?.[selectedFailIndex]} />
              </>
            )}
            {(!failData || failData?.length === 0) && (
              <ParticipantEmpty text="실패한 챌린지가 없습니다" />
            )}
          </DataContainer>
        </div>
      </Dialog>
    </>
  );
}
